import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDashboardLayouts, type DashboardLayoutResponse } from '../hooks/useDashboardLayout';
import { cn } from '../../../lib/utils';

const ChevronDown = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const GearIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ResetIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

export interface DashboardSwitcherProps {
  currentLayoutName: string | undefined;
  onSaveAsClick: () => void;
  onManageClick?: () => void;
  onResetLayoutClick?: () => void;
}

const DashboardSwitcher: React.FC<DashboardSwitcherProps> = ({
  currentLayoutName,
  onSaveAsClick,
  onManageClick,
  onResetLayoutClick,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { layouts, isLoading } = useDashboardLayouts();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const defaultLayout = layouts.find((l) => l.isDefault);
  const currentDisplayName = currentLayoutName
    ? layouts.find((l) => l.name === currentLayoutName)?.name ?? currentLayoutName
    : defaultLayout?.name ?? 'Dashboard';

  const setDashboard = (name: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set('dashboard', name);
    else next.delete('dashboard');
    navigate({ pathname: '/dashboard', search: next.toString() });
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 min-h-[44px] sm:min-h-0 text-sm font-medium transition-colors',
          colors.border,
          colors.surface,
          colors.text,
          colors.interactiveHover,
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Switch dashboard"
      >
        <span className="truncate max-w-[140px] sm:max-w-[200px]">{currentDisplayName}</span>
        <ChevronDown className="w-4 h-4 shrink-0" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border shadow-lg py-1 max-h-[min(60vh,320px)] overflow-y-auto',
            colors.border,
            colors.surface,
          )}
          role="listbox"
        >
          {isLoading ? (
            <div className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading…</div>
          ) : (
            <>
              {layouts.map((layout: DashboardLayoutResponse) => {
                const isSelected =
                  (currentLayoutName && layout.name === currentLayoutName) ||
                  (!currentLayoutName && layout.isDefault);
                return (
                  <button
                    key={layout.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setDashboard(layout.isDefault ? null : layout.name)}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                      isSelected
                        ? cn(colors.surfaceHover, colors.primaryFg)
                        : cn(colors.text, colors.interactiveHover),
                    )}
                  >
                    <span className="truncate">{layout.name}</span>
                    {layout.isDefault && (
                      <span className={cn('text-xs shrink-0', colors.textMuted)}>Default</span>
                    )}
                    {isSelected && <CheckIcon className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
              <div className={cn('border-t my-1', colors.border)} />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSaveAsClick();
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium transition-colors',
                  colors.primaryFg,
                  colors.interactiveHover,
                )}
              >
                <PlusIcon className="w-4 h-4" />
                Save as new dashboard
              </button>
              {onResetLayoutClick != null && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onResetLayoutClick();
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                >
                  <ResetIcon className="w-4 h-4" />
                  Reset layout to default
                </button>
              )}
              {onManageClick && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onManageClick();
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                >
                  <GearIcon className="w-4 h-4" />
                  Manage dashboards
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSwitcher;
